/**
 * Enumerations and constants shared across the Swarved backend and web
 * app. Data-transfer shapes are inferred from the tRPC router on the
 * web side (`inferRouterOutputs`), so this file holds only the closed
 * vocabularies both sides must agree on.
 */

/** Permission tiers. `learner` is any signed-in user. */
export const ROLES = ["learner", "helper", "admin"] as const;
export type Role = (typeof ROLES)[number];

export function isRole(value: unknown): value is Role {
  return (
    typeof value === "string" && (ROLES as readonly string[]).includes(value)
  );
}

/** Which script a captured doha photo is in. */
export const LANGUAGES = ["hindi", "english"] as const;
export type Language = (typeof LANGUAGES)[number];

/**
 * An annotation either links a word on the page to a known glossary
 * term (`linked`) or flags a word the reader doesn't understand and
 * wants help with (`undefined`).
 */
export const ANNOTATION_STATUSES = ["linked", "undefined"] as const;
export type AnnotationStatus = (typeof ANNOTATION_STATUSES)[number];

/** What a discussion thread hangs off. */
export const SUBJECT_TYPES = ["annotation", "term", "doha"] as const;
export type SubjectType = (typeof SUBJECT_TYPES)[number];

/** The current API version for this project. */
export const API_VERSION = "0.1.0";

/**
 * A normalised box drawn on a photo, stored as fractions of the image
 * dimensions (0..1) so it survives resizing and different display
 * sizes. `null` box = a chip-only annotation with no on-image position.
 */
export interface AnnotationBox {
  x: number;
  y: number;
  w: number;
  h: number;
}
